import React, { useEffect, useRef, useState } from "react";
import { dms, inr, niceDate } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2 } from "lucide-react";

// Point 92: link a payment to one or more pending invoices of a retailer
export default function LinkInvoiceButton({ retailerId, amount, setAmount, value = [], onChange, className = "h-7 text-xs gap-1" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({}); // invoice_id -> amount (string)
  const prevRetailer = useRef(retailerId);

  // retailer changed -> old links no longer valid
  useEffect(() => {
    if (prevRetailer.current !== retailerId) { prevRetailer.current = retailerId; onChange([]); }
  }, [retailerId]); // eslint-disable-line

  const payAmt = Number(amount) || 0;
  const linkedTotal = value.reduce((a, x) => a + (Number(x.amount) || 0), 0);
  const selTotal = Object.values(sel).reduce((a, x) => a + (Number(x) || 0), 0);

  const openDialog = async () => {
    if (!retailerId) { toast.error("Select retailer first"); return; }
    const init = {};
    value.forEach(v => { init[v.invoice_id] = String(v.amount); });
    setSel(init);
    setOpen(true);
    setLoading(true);
    try {
      const d = await dms.secondaryPendingInvoices(retailerId);
      setRows(d.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load invoices");
      setRows([]);
    }
    setLoading(false);
  };

  const toggle = (r) => {
    const next = { ...sel };
    if (next[r.invoice_id] !== undefined) {
      delete next[r.invoice_id];
    } else {
      let a = r.balance_due;
      if (payAmt > 0) a = Math.min(r.balance_due, Math.round((payAmt - selTotal) * 100) / 100);
      if (a <= 0) { toast.error("Payment amount is fully allocated. Increase the amount or edit the linked amounts."); return; }
      next[r.invoice_id] = String(a);
    }
    setSel(next);
  };

  const done = () => {
    const items = [];
    for (const r of rows) {
      const a = Number(sel[r.invoice_id]);
      if (sel[r.invoice_id] !== undefined && a > 0) {
        if (a > r.balance_due + 0.01) { toast.error(`${r.bill_no}: amount is more than pending ${inr(r.balance_due)}`); return; }
        items.push({ invoice_id: r.invoice_id, amount: a });
      }
    }
    const total = items.reduce((x, y) => x + y.amount, 0);
    if (payAmt > 0 && total > payAmt + 0.01) { toast.error(`Linked ${inr(total)} is more than payment amount ${inr(payAmt)}`); return; }
    if (payAmt <= 0 && total > 0) setAmount(total);
    onChange(items);
    setOpen(false);
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" className={className} onClick={openDialog} data-testid="link-invoice-btn">
        <Link2 size={12} />
        {value.length ? `Linked: ${value.length} invoice${value.length > 1 ? "s" : ""} · ${inr(linkedTotal)}` : "Link invoice"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Link payment to invoices</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y border rounded-lg">
            {loading && <div className="p-4 text-xs text-slate-500 text-center">Loading…</div>}
            {!loading && rows.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">No pending invoices for this retailer</div>}
            {rows.map(r => {
              const on = sel[r.invoice_id] !== undefined;
              return (
                <div key={r.invoice_id} className={`p-3 text-sm ${on ? "bg-[#faf6e6]" : ""}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggle(r)} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-mono font-medium">{r.bill_no}</span>
                        <span className="text-xs text-slate-500">{niceDate(r.date)}</span>
                      </div>
                      <div className="text-xs text-slate-500">Total {inr(r.total)} · Received {inr(r.received_amount)} · <span className="text-rose-700 font-semibold">Pending {inr(r.balance_due)}</span></div>
                    </div>
                  </label>
                  {on && (
                    <div className="mt-2 ml-7 flex items-center gap-2">
                      <span className="text-xs text-slate-500">Amount against this invoice</span>
                      <Input type="number" step="0.01" value={sel[r.invoice_id]} className="h-8 w-32"
                        onChange={e => setSel({ ...sel, [r.invoice_id]: e.target.value })} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-sm flex justify-between">
            <span className="text-slate-500">Total linked</span>
            <span className="font-semibold">{inr(selTotal)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { onChange([]); setSel({}); setOpen(false); }}>Clear link</Button>
            <Button onClick={done} className="bg-[#a67c00] hover:bg-[#8a6600] text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
